import { HOTEL_MAX_STAY_NIGHTS } from "./types";

/**
 * Okno pobytu hotelové poptávky — JEDINÁ implementace pravidla „od kdy do kdy" (vize D3, řez 08).
 *
 * Pravidlo má tři konzumenty a všichni musí říkat totéž slovo od slova, protože jeden zákazník
 * je potká za sebou: rezervační widget ho kontroluje před odesláním (aby nešel POST, který
 * server stejně odmítne), `inquirySchema` při parsování (klient i server, tentýž kód) a
 * `resolveHotelSelection` jako poslední obrana před zápisem snapshotu do leadu. Dřív to byly tři
 * kopie držené pohromadě komentářem s číslem řádku — a už se rozešly (schéma zapomnělo na horní
 * mez). Proto je pravidlo tady: bez DB, bez env, čistě testovatelné, jeden zdroj znění.
 */

const MS_PER_DAY = 24 * 60 * 60 * 1000;

const STAY_DAY_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;

/**
 * Které pole chybu vlastní. Zod ji podle toho zavěsí na `path`, widget podle téhož klíče
 * najde souhrnný řádek — jinak by chyba skončila u špatného ovladače.
 */
export interface StayWindowError {
  field: "requestedDate" | "stayTo";
  message: string;
}

export type StayWindowResult =
  | { ok: true; stayFrom: string; stayTo: string; nights: number }
  | { ok: false; error: StayWindowError };

/**
 * Půlnoc UTC daného kalendářního dne, nebo null u data, které v kalendáři neexistuje
 * (`2026-13-45`). Kontrola zpětným převodem — `Date.UTC` by takový vstup tiše přetekl na jiný
 * den a poptávka by dostala termín, který zákazník nezadal. UTC drží počet nocí imunní vůči
 * letnímu času.
 */
function parseStayDay(iso: string): number | null {
  const match = STAY_DAY_PATTERN.exec(iso);
  if (!match) return null;
  const [, year, month, day] = match;
  const timestamp = Date.UTC(Number(year), Number(month) - 1, Number(day));
  const date = new Date(timestamp);
  if (
    date.getUTCFullYear() !== Number(year) ||
    date.getUTCMonth() !== Number(month) - 1 ||
    date.getUTCDate() !== Number(day)
  ) {
    return null;
  }
  return timestamp;
}

const invalid = (field: StayWindowError["field"], message: string): StayWindowResult => ({
  ok: false,
  error: { field, message },
});

/**
 * Vyhodnotí okno pobytu a spočítá noci. Volá ho server (potřebuje `nights` do snapshotu);
 * plochy, kterým stačí chyba, mají `validateStayWindow`.
 */
export function resolveStayWindow(from?: string | null, to?: string | null): StayWindowResult {
  if (!from) return invalid("requestedDate", "Vyberte prosím datum začátku pobytu.");
  if (!to) return invalid("stayTo", "Vyberte prosím datum konce pobytu.");

  const start = parseStayDay(from);
  const end = parseStayDay(to);
  if (start === null) return invalid("requestedDate", "Zadejte prosím platný termín pobytu.");
  if (end === null) return invalid("stayTo", "Zadejte prosím platný termín pobytu.");

  const nights = Math.round((end - start) / MS_PER_DAY);
  if (nights < 1) return invalid("stayTo", "Konec pobytu musí být po jeho začátku.");
  if (nights > HOTEL_MAX_STAY_NIGHTS) {
    return invalid("stayTo", `Pobyt může trvat nejvýše ${HOTEL_MAX_STAY_NIGHTS} nocí.`);
  }

  return { ok: true, stayFrom: from, stayTo: to, nights };
}

/** Chyba okna pobytu, nebo null když je v pořádku — pohled pro validátory (Zod, widget). */
export function validateStayWindow(
  from?: string | null,
  to?: string | null,
): StayWindowError | null {
  const result = resolveStayWindow(from, to);
  return result.ok ? null : result.error;
}
